/**
 * New Year paper crafts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearPaperCraftsPack: ToilePack = {
    slug: 'new-year-paper-crafts',
    category: 'season',
    name: 'New Year paper crafts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hat', label: 'Party hat', subject: 'a template for a cone party hat with a patterned fan shape lettered "2026" along the base band, a glue tab and a fold line'},
        {key: 'popper', label: 'Popper wrap', subject: 'a template for a confetti popper wrap lettered "POP!" along one end, a patterned rectangle and end tabs'},
        {key: 'star', label: 'Paper star', subject: 'a template for a 3D paper star with one point lettered "2026", fold lines and tabs'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical sheet with a gold border and an even margin, the template centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical sheet with a confetti border and a white margin, the template centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical sheet with a wide margin, the template centred, print-ready.'},
    ],
    directions: ['as a crown instead', 'for a birthday instead', 'sized for a table setting'],
    directionHint: 'try: as a crown instead',
    seo: {
        title: 'New Year Paper Crafts: Hat, Popper & Star Template',
        metaDescription: 'Nine New Year paper-craft templates — a cone party hat, a confetti popper wrap, and a 3D paper star — fold and cut lines drawn, print-ready.',
        h1: 'New Year paper crafts: hat, popper, and star templates',
        tagline: 'Nine templates, three crafts, three styles each — fold and cut lines drawn, print-ready.',
        intro: 'Three New Year paper-craft templates — a cone party hat, a confetti popper wrap, and a 3D paper star — each with fold and cut lines marked, in black-and-gold, confetti-party, and minimal styles. Ask for a different craft or a group-size version, and the same template grammar carries it.',
        useCases: [
            "a New Year's Eve party craft table",
            'a classroom countdown activity',
            'a family party favor project',
            'a photo-booth prop',
        ],
        faq: [
            {q: 'Do the fold and cut lines actually print on the page?', a: 'Yes — every fold line and cut line is marked directly on the template.'},
            {q: 'What size do these print at?', a: 'Each template is a single page; scale it at your printer for a bigger or smaller version.'},
            {q: 'Can I get a different craft or a group-size version?', a: 'Yes — the direction field takes any variation, including a different craft or a resized template.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
